import {isRight} from 'fp-ts/lib/Either'
import React, {useEffect, useState} from 'react'
import {Helmet} from 'react-helmet-async'

import {
  CardActions,
  CardActionsCodecV2,
  CardActionsV2
} from '../../../common/types'
import {PreviewFlagMarker} from '../../../components/feature-flags'
import {Form} from '../../../components/form'
import {InputGroup} from '../../../components/library/form'
import {Icon} from '../../../components/library/icon/icon'
import {NoResultsPermissions} from '../../../components/library/list'
import styled from 'styled-components'
import {Switch} from '../../../components/library/toggles/switch'
import {Loader} from '../../../components/loader'
import {
  useEnvironmentByOrganisationIdAndIdQuery,
  useSaveEnvironmentActionConfigurationMutation
} from '../../../data/workbench-api/generated/environment-card-actions'
import {
  PermissionAction,
  PermissionResource
} from '../../../data/workbench-api/generated/types'
import {EnvironmentNode} from '../../../environment'
import {AlertDialog} from '../../../hooks/use-confirm'
import {useStateChanged} from '../../../hooks/use-state-changed'
import {useAnalytics} from '../../../lib/analytics/analytics'
import {useToast} from '../../../lib/toast'
import {IconHeading, Section, SectionContent, SectionInfo} from '../components'

const IconLabel = styled.div`
  display: inline-flex;
  gap: 10px;
`
export const CardActionsConfiguration = ({
  environment
}: {
  environment: EnvironmentNode
}) => {
  const [ready, setReady] = useState(false)
  const [localActionConfig, setLocalActionConfig, changed, resetState] =
    useStateChanged<CardActionsV2>({})

  const toast = useToast()
  const analytics = useAnalytics()

  const canView = environment.hasPermission(
    PermissionResource.Environment,
    PermissionAction.View
  )
  const canEdit = environment.hasPermission(
    PermissionResource.Environment,
    PermissionAction.Admin
  )

  const validate = (val: CardActions): val is CardActions => {
    const decode = CardActionsCodecV2.decode(val)
    return isRight(decode)
  }

  const {data: actionQuery, loading} = useEnvironmentByOrganisationIdAndIdQuery(
    {
      variables: {
        environmentId: environment.id,
        organisationId: environment.organisationId
      },
      onCompleted: data => {
        const actionConfig = CardActionsCodecV2.decode(
          data.environmentByOrganisationIdAndId?.actionConfiguration
        )
        if (isRight(actionConfig)) {
          resetState(actionConfig.right)
        }
        setReady(true)
      }
    }
  )

  const [doMutation, {loading: saving, error}] =
    useSaveEnvironmentActionConfigurationMutation({
      onCompleted: data => {
        if (data.updateEnvironment?.environment?.actionConfiguration) {
          resetState(data.updateEnvironment?.environment?.actionConfiguration)
          analytics.track({
            event: 'Edited configuration',
            properties: {
              org_id: environment.organisationId,
              environment_id: environment.id,
              category: 'configuration>card-templates>card-actions'
            }
          })
        }
        toast('Changes saved')
      }
    })

  const onSave = async () => {
    if (validate(localActionConfig)) {
      await doMutation({
        variables: {
          environmentId: environment.id,
          actionConfiguration: localActionConfig
        }
      })
    }
  }

  const onReset = async () => {
    const actionConfig = CardActionsCodecV2.decode(
      actionQuery?.environmentByOrganisationIdAndId?.actionConfiguration
    )
    if (isRight(actionConfig)) {
      resetState(actionConfig.right)
    }
  }

  useEffect(() => {
    analytics.track({
      event: 'Viewed card templates actions configuration',
      properties: {
        org_id: environment.organisationId,
        environment_id: environment.id,
        category: 'configuration>card-templates>card-actions'
      }
    })
  }, [analytics, environment])

  return (
    <Section>
      <Helmet>
        <title>Card actions</title>
      </Helmet>
      <header></header>
      <header>
        <IconHeading>
          <Icon name="click" /> Card actions <PreviewFlagMarker />
        </IconHeading>
      </header>

      <SectionInfo>
        <p>
          Configure the card actions enabled in each location of the environment
        </p>
      </SectionInfo>

      <SectionContent>
        {!ready ? (
          <Loader />
        ) : canView ? (
          <Form
            onSubmit={async () => onSave()}
            submitText="Update"
            cancelText="Discard changes"
            onCancel={changed ? onReset : undefined}
            error={error}
            isValid={changed && !loading && validate(localActionConfig)}
            loading={loading || saving}
          >
            <h4>Menu actions (overflow)</h4>
            <InputGroup>
              <Switch
                disabled={!canEdit}
                label={
                  <IconLabel>
                    <Icon name={'remove'} />
                    Dismiss
                  </IconLabel>
                }
                isForm={true}
                labelPosition="left"
                checked={!!localActionConfig.dismiss?.overflow.enabled}
                onChange={enabled =>
                  setLocalActionConfig(c => {
                    return {
                      ...c,
                      dismiss: c.dismiss
                        ? {
                            ...c.dismiss,
                            overflow: {enabled}
                          }
                        : {
                            swipe: {enabled: false},
                            overflow: {enabled}
                          }
                    }
                  })
                }
              />
              <Switch
                disabled={!canEdit}
                label={
                  <IconLabel>
                    <Icon name={'time'} />
                    Snooze
                  </IconLabel>
                }
                isForm={true}
                labelPosition="left"
                checked={!!localActionConfig.snooze?.overflow.enabled}
                onChange={enabled =>
                  setLocalActionConfig(c => {
                    return {
                      ...c,
                      snooze: c.snooze
                        ? {
                            ...c.snooze,
                            overflow: {enabled}
                          }
                        : {
                            swipe: {enabled: false},
                            overflow: {enabled}
                          }
                    }
                  })
                }
              />
              <Switch
                disabled={!canEdit}
                label={
                  <IconLabel>
                    <Icon name={'thumbsUp'} />
                    Vote up
                  </IconLabel>
                }
                isForm={true}
                labelPosition="left"
                checked={!!localActionConfig.voteUp?.overflow.enabled}
                onChange={enabled =>
                  setLocalActionConfig(c => {
                    return {
                      ...c,
                      voteUp: c.voteUp
                        ? {
                            ...c.snooze,
                            overflow: {enabled}
                          }
                        : {
                            overflow: {enabled}
                          }
                    }
                  })
                }
              />
              <Switch
                disabled={!canEdit}
                label={
                  <IconLabel>
                    <Icon name={'thumbsDown'} />
                    Vote down
                  </IconLabel>
                }
                isForm={true}
                labelPosition="left"
                checked={!!localActionConfig.voteDown?.overflow.enabled}
                onChange={enabled =>
                  setLocalActionConfig(c => {
                    return {
                      ...c,
                      voteDown: c.voteDown
                        ? {
                            ...c.voteDown,
                            overflow: {enabled}
                          }
                        : {
                            overflow: {enabled}
                          }
                    }
                  })
                }
              />
            </InputGroup>

            <h4>Quick actions (mobile)</h4>
            <InputGroup>
              <Switch
                disabled={!canEdit}
                label={
                  <IconLabel>
                    <Icon name={'remove'} />
                    Dismiss
                  </IconLabel>
                }
                isForm={true}
                labelPosition="left"
                checked={!!localActionConfig.dismiss?.swipe.enabled}
                onChange={enabled =>
                  setLocalActionConfig(c => {
                    return {
                      ...c,
                      dismiss: c.dismiss
                        ? {
                            ...c.dismiss,
                            swipe: {enabled}
                          }
                        : {
                            swipe: {enabled},
                            overflow: {enabled: false}
                          }
                    }
                  })
                }
              />
              <Switch
                disabled={!canEdit}
                label={
                  <IconLabel>
                    <Icon name={'time'} />
                    Snooze
                  </IconLabel>
                }
                isForm={true}
                labelPosition="left"
                checked={!!localActionConfig.snooze?.swipe.enabled}
                onChange={enabled =>
                  setLocalActionConfig(c => {
                    return {
                      ...c,
                      snooze: c.snooze
                        ? {
                            ...c.snooze,
                            swipe: {enabled}
                          }
                        : {
                            swipe: {enabled},
                            overflow: {enabled: false}
                          }
                    }
                  })
                }
              />
            </InputGroup>
          </Form>
        ) : (
          <div style={{margin: '50px 0px'}}>
            <NoResultsPermissions message="Sorry, you don't have permission to view these settings" />
          </div>
        )}
      </SectionContent>
      <AlertDialog
        isBlocking={changed}
        onSave={validate(localActionConfig) ? onSave : undefined}
      >
        Would you like to save configuration before you leave?
      </AlertDialog>
    </Section>
  )
}
